// src/pages/Services.js

import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import ZaraAssistant from "../components/ZaraAssistant";
import ZaraChatWidget from "../components/ZaraChatWidget";
import FloatingZaraButton from "../components/FloatingZaraButton";
import { useLanguage } from "../context/LanguageContext";
import translations from "../data/translations";
import useSubscriptions from "../hooks/useSubscriptions";
import CustomerHistory from "../components/CustomerHistory";
import { getCustomerHistory } from "../services/customerHistoryApi";

const SkeletonCard = () => (
  <div className="rounded-3 p-3 mb-3" style={{ border: "1.5px solid #e6eef3", background: "#fafcfe" }}>
    <div className="d-flex align-items-start gap-3">
      <div style={{ width: 48, height: 48, borderRadius: 10, background: "#eee", flexShrink: 0 }} />
      <div style={{ flex: 1 }}>
        <div style={{ height: 14, width: "60%", background: "#eee", borderRadius: 6, marginBottom: 8 }} />
        <div style={{ height: 11, width: "40%", background: "#f4f4f4", borderRadius: 6, marginBottom: 6 }} />
        <div style={{ height: 11, width: "35%", background: "#f4f4f4", borderRadius: 6 }} />
      </div>
      <div style={{ width: 70, textAlign: "right" }}>
        <div style={{ height: 11, background: "#f4f4f4", borderRadius: 6, marginBottom: 6 }} />
        <div style={{ height: 14, background: "#eee", borderRadius: 6 }} />
      </div>
    </div>
  </div>
);

const EmptyState = ({ t, network }) => (
  <div className="text-center py-5">
    <div style={{ fontSize: "3rem", marginBottom: 12 }}>🔍</div>
    <p style={{ fontWeight: 700, fontSize: "0.95rem", color: "#1a1a2e", marginBottom: 4 }}>
      {t.noServicesTitle}
    </p>
    <p style={{ fontSize: "0.8rem", color: "#888" }}>
      {t.noServicesSub.replace("{network}", network)}
    </p>
  </div>
);

const ErrorState = ({ message, onRetry, t }) => (
  <div className="text-center py-4">
    <div style={{ fontSize: "2.5rem", marginBottom: 12 }}>⚠️</div>
    <p style={{ fontWeight: 700, fontSize: "0.92rem", color: "#e74c3c", marginBottom: 6 }}>API Error</p>
    <p style={{ fontSize: "0.8rem", color: "#888", marginBottom: 16 }}>{message}</p>
    <button
      onClick={onRetry}
      style={{
        padding: "9px 24px", background: "#0099CC", color: "white",
        border: "none", borderRadius: 8, fontWeight: 600,
        fontSize: "0.85rem", cursor: "pointer",
      }}
    >
      {t.retryBtn || "Retry 🔄"}
    </button>
  </div>
);

const ServiceCard = ({ svc, isSelected, onSelect, t }) => {
  return (
    <div
      onClick={() => onSelect(svc)}
      className="rounded-3 p-3 mb-3"
      style={{
        cursor: "pointer",
        border: isSelected ? "2px solid #0099CC" : "1.5px solid #e6eef3",
        background: isSelected ? "#f0faff" : "#fafcfe",
        transition: "border 0.15s, background 0.15s",
      }}
    >
      <div className="d-flex align-items-start gap-3">
        <div
          className="rounded-3 d-flex align-items-center justify-content-center flex-shrink-0"
          style={{ width: 48, height: 48, background: svc.color + "22", fontSize: "1.4rem" }}
        >
          {svc.icon}
        </div>

        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="d-flex align-items-center gap-2 flex-wrap mb-1">
            <span style={{ fontWeight: 700, fontSize: "0.88rem", color: "#1a1a2e" }}>{svc.name}</span>
            <span
              style={{
                background: "#edfaf3",
                color: "#27ae60",
                fontSize: "0.68rem",
                fontWeight: 700,
                borderRadius: "20px",
                border: "1px solid #b2dfdb",
                padding: "1px 8px",
              }}
            >
              {svc.status}
            </span>
            {svc.subMode && (
              <span
                style={{
                  background: "#f0f0f0",
                  color: "#666",
                  fontSize: "0.62rem",
                  fontWeight: 600,
                  borderRadius: "20px",
                  padding: "1px 7px",
                }}
              >
                {svc.subMode}
              </span>
            )}
          </div>

          <p style={{ fontSize: "0.74rem", color: "#999", marginBottom: "2px" }}>
            {t.serviceType || "Type"}: {svc.type}
          </p>
          <p style={{ fontSize: "0.74rem", color: "#999", marginBottom: 0 }}>
            {t.subscribedOn || "Subscribe on"}: {svc.subscribedOn}
          </p>
        </div>
      </div>
    </div>
  );
};

const Services = () => {
  const navigate = useNavigate();
  const { phone = "03XX-XXXXXXXX", network = "Jazz" } = useLocation().state || {};

  const { language } = useLanguage();
  const t = translations[language] || translations["English"];
  const isEn = language === "English";

  const [selectedService, setSelectedService] = useState(null);
  const { services, loading, error, retry } = useSubscriptions(phone, network);

  const ACTIONS = [
    { id: "cancel", label: t.cancelLabel, sub: t.cancelSub, icon: "🔄", color: "#e74c3c" },
    // { id: "claim",     label: t.claimLabel,     sub: t.claimSub,     icon: "📋", color: "#0099CC" },
    { id: "complaint", label: t.complaintLabel, sub: t.complaintSub, icon: "⚠️", color: "#e67e22" },
    { id: "feedback", label: t.feedbackLabel, sub: t.feedbackSub, icon: "💚", color: "#2ecc71" },
  ];

  const handleAction = (actionId) => {
    const state = { service: selectedService, phone, network };
    if (actionId === "cancel") navigate("/cancel-refund", { state });
    // if (actionId === "claim")     navigate("/claim",         { state });
    if (actionId === "complaint") navigate("/complaint", { state });
    if (actionId === "feedback") navigate("/feedback", { state });
  };

  const [customerHistory, setCustomerHistory] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState("");
  const [showHistoryModal, setShowHistoryModal] = useState(false);

  useEffect(() => {
    let mounted = true;
    const fetchHistory = async () => {
      try {
        setHistoryLoading(true);
        const response = await getCustomerHistory(phone, network);
        console.log("Customer History Response:", response);

        if (mounted) {
          setCustomerHistory(response);
        }
      } catch (error) {
        if (mounted) {
          setHistoryError(error.message);
        }
      } finally {
        if (mounted) {
          setHistoryLoading(false);
        }
      }
    };
    if (phone && network) {
      fetchHistory();
    }
    return () => {
      mounted = false;
    };
  }, [phone, network]);

  const networkColors = {
    jazz: "#ff6600", zong: "#d50032", telenor: "#0067b1",
    ufone: "#6a0dad", easypaisa: "#00a86b", jazzcash: "#c0392b",
    tabeeb: "#3498db"
  };
  const badgeColor = networkColors[network.toLowerCase()] || "#888";
  const allDisabled = !selectedService;

  return (
    <div className="container-fluid py-4 px-3 px-md-4" style={{ maxWidth: "900px", margin: "0 auto" }}>
      <Header showBack onBack={() => navigate("/")} />

      <div className="card shadow-sm border-0 rounded-4 p-4 mb-4">

        {/* Step header */}
        <div className="d-flex align-items-center justify-content-between gap-3 mb-4">
          <div className="d-flex justify-content-between align-items-center w-100">

            {/* Left Side */}
            <div className="d-flex align-items-center gap-3">
              <div
                className="d-flex align-items-center justify-content-center rounded-circle text-white fw-bold flex-shrink-0"
                style={{
                  width: 42,
                  height: 42,
                  background: "#0099CC",
                  fontSize: "1rem",
                }}
              >
                2
              </div>

              <div>
                <p
                  style={{
                    fontWeight: 700,
                    fontSize: "1rem",
                    color: "#1a1a2e",
                    marginBottom: "2px",
                  }}
                >
                  {t.activeServices} ({network})
                </p>

                <p
                  style={{
                    fontSize: "0.78rem",
                    color: "#888",
                    marginBottom: 0,
                  }}
                >
                  {loading
                    ? t.loadingServices
                    : t.serviceFound.replace("{phone}", phone)}
                </p>
              </div>
            </div>

            {/* Right Side */}
            {(customerHistory?.data?.complaints?.length > 0 ||
              customerHistory?.data?.cancellations?.length > 0) && (
                <button
                  className="btn btn-outline-primary btn-sm ms-auto"
                  onClick={() => setShowHistoryModal(true)}
                >
                  📜 History
                </button>
              )}

          </div>
        </div>

        {loading && <><SkeletonCard /><SkeletonCard /></>}
        {!loading && error && <ErrorState message={error} onRetry={retry} t={t} />}
        {!loading && !error && services.length === 0 && <EmptyState t={t} network={network} />}

        {!loading && !error && services.length > 0 && (
          <>
            {services.map((svc) => (
              <ServiceCard key={svc.id} svc={svc} isSelected={selectedService?.id === svc.id} onSelect={setSelectedService} t={t} />
            ))}

            {/* Hint banner */}
            <div style={{
              fontSize: "0.78rem",
              color: allDisabled ? "#e67e22" : "#27ae60",
              background: allDisabled ? "#fff8f0" : "#edfaf3",
              border: `1px solid ${allDisabled ? "#fde8c8" : "#b2dfdb"}`,
              borderRadius: 8, padding: "8px 12px", marginBottom: 16,
              transition: "all 0.3s",
            }}>
              {allDisabled
                ? `💡 ${t.selectServiceHint}`
                : `✅ ${t.serviceSelected.replace("{name}", selectedService?.name)}`}
            </div>

            <h6 className="fw-semibold mb-3" style={{ color: "#1a1a2e" }}>{t.whatToDo}</h6>

            <div className="row g-2 mb-4">
              {ACTIONS.map((a) => (
                <div key={a.id} className="col-6 col-md-3">
                  <button
                    disabled={allDisabled}
                    onClick={() => handleAction(a.id)}
                    className="w-100 rounded-3 p-3 d-flex flex-column align-items-center justify-content-center border-0"
                    style={{
                      background: "#fff", minHeight: "120px",
                      boxShadow: "0 2px 10px rgba(0,0,0,0.07)",
                      cursor: allDisabled ? "not-allowed" : "pointer",
                      opacity: allDisabled ? 0.45 : 1,
                      transition: "transform 0.15s, box-shadow 0.15s, opacity 0.3s",
                      outline: "none",
                    }}
                    onMouseEnter={e => {
                      if (!allDisabled) {
                        e.currentTarget.style.transform = "translateY(-2px)";
                        e.currentTarget.style.boxShadow = "0 6px 18px rgba(0,0,0,0.12)";
                      }
                    }}
                    onMouseLeave={e => {
                      e.currentTarget.style.transform = "translateY(0)";
                      e.currentTarget.style.boxShadow = "0 2px 10px rgba(0,0,0,0.07)";
                    }}
                  >
                    <div style={{ fontSize: "1.8rem", marginBottom: 6 }}>{a.icon}</div>
                    <div style={{
                      fontWeight: 700, fontSize: "0.78rem",
                      color: allDisabled ? "#bbb" : a.color,
                      marginBottom: 3, textAlign: "center", transition: "color 0.3s",
                    }}>
                      {a.label}
                    </div>
                    <small style={{ color: "#888", fontSize: "0.7rem", textAlign: "center", lineHeight: 1.3 }}>
                      {a.sub}
                    </small>
                  </button>
                </div>
              ))}
            </div>
          </>
        )}

        {/* ── Single shared ZaraChatWidget for the whole page ──
            Both the ZaraAssistant card AND the floating button open
            the SAME chat drawer / session — no duplicate instances,
            no resetting when switching triggers. */}
        <ZaraChatWidget isEn={isEn} initialPhone={phone} initialNetwork={network} skipAuth>
          {(openChat) => (
            <>
              <ZaraAssistant
                message={t.ZARAMessage}
                features={t.ZARAFeatures || ["Quick & Easy", "Safe & Secure", "24/7 Support", "Trusted by Millions"]}
                openChat={openChat}
              />
              {/* <FloatingZaraButton onClick={openChat} isEn={isEn} /> */}
            </>
          )}
        </ZaraChatWidget>
      </div>

      {showHistoryModal && (
        <div className="modal fade show d-block" tabIndex="-1"
          style={{ background: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog modal-lg modal-dialog-scrollable">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  Customer History
                </h5>
                <button type="button" className="btn-close" onClick={() => setShowHistoryModal(false)} />
              </div>
              <div className="modal-body">
                <CustomerHistory
                  history={customerHistory}
                  loading={historyLoading}
                  error={historyError}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default Services;